<template>

  <v-row>
    <v-col cols="12">
      <v-table-toolbar>
        <v-spacer/>
        <v-btn-primary
          v-if="isAdminOrRoot"
          prepend-icon="far fa-plus"
          @click="showCreateInstitution = true"
          data-pw="createInstitutionButton"
        >
          {{ $t("buttons.create") }}
        </v-btn-primary>
      </v-table-toolbar>
    </v-col>
  </v-row>
  <v-row>
    <v-col md="=3" lg="2">
      <table-filter-card  @resetFilters="handleReset" :active-filter-count="activeFilterCount">
        <table-filter-card-item title="Search">
          <search-text-field
            :label="$t('common.search_title')"
            :disabled="store.networkBusy"
            :search-string="searchString"
            @update:text="text => {
              handleSearch(text)
            }"
          />
        </table-filter-card-item>
        <table-filter-card-item title="Tenant" v-if="!hiddenFields.includes('tenant')">
          <tenant-search-autocomplete
            v-model="selectedTenant"
            :disabled="lockTenantFilter"
            entity-type="tenant"
          />
        </table-filter-card-item>
      </table-filter-card>
    </v-col>
    <v-col md="9" lg="10">
      <v-data-table-server
        class="mb-6"
        density="compact"
        :loading="loadingData"
        :headers="headers"
        :items="institutions"
        :items-per-page="perPage"
        :items-length="0"
        @click:row="onClickRow"
        @update:sort-by="onSort"
        :sort-by="sort"
        :hover="true"
        hide-default-footer
      >
        <template v-slot:no-data>
          <h5 class="pa-2 d-flex align-center justify-center align-items-center">
            {{ $t("common.no_data_message") }}
          </h5>
        </template>
        <template v-slot:loader>
          <v-sheet
            class="d-flex align-center justify-center"
            height="100%"
          >
            <v-progress-linear
              indeterminate
              color="primary"
            ></v-progress-linear>
          </v-sheet>
        </template>

        <template v-slot:item.createdAt="{ item }">
          {{ toLocaleDateTime(item["createdAt"]) }}
        </template>

        <template v-slot:item.actions="{ item }">
          <v-icon @click="openInstitutionDetails(item._id as string)">
            far fa-pen-to-square
          </v-icon>
        </template>

        <template v-slot:bottom>
          <v-divider/>
          <pagination-controls
            v-if="paginatedData"
            class="text-center pt-2 v-table__pagination-controls"
            :pagination-footer-data="paginationFooterData"
            @update:per-page="onUpdatePerPage"
            @on-next="onNext"
            @on-prev="onPrev"
            @on-page-change="onPageChange"
            data-pw="institutionPaginationControls"
          />
        </template>
      </v-data-table-server>

    </v-col>
  </v-row>

  <v-dialog v-model="showCreateInstitution" persistent max-width="500">
    <create-institution-card
      :allowed="store.hasAbility(Entity.Institution, Action.create) || isAdminOrRoot"
      data-pw="createInstitutionCard"
      @close="onCloseCreateDialog"/>
  </v-dialog>
</template>

<script lang="ts" setup>
import {computed, onMounted, PropType, Ref, ref, watch} from "vue";
import yapi from "@/lib/yapi";
import {useI18n} from "vue-i18n";
import {useRoute, useRouter} from "vue-router";
import {
  Action,
  Entity,
  Institution,
  InstitutionPaginationParams,
  isYapiError,
  Paginated,
  Role,
  Tenant,
  YapiError
} from "@YenzaCT/sdk";

import PaginationControls from "@/components/PaginationControls.vue";
import SearchTextField from "@/components/SearchTextField.vue";

import {useGlobalStore} from "@/store";
import {ColumnsConfig, FooterData, footerDataFactory, usePagination} from "@/lib/pagination";
import {toLocaleDateTime} from "@/lib/time";
import CreateInstitutionCard from "@/components/CreateCards/CreateInstitutionCard.vue";
import TableFilterCard from "@/components/TableFilterCard.vue";
import TableFilterCardItem from "@/components/TableFilterCardItem.vue";
import TenantSearchAutocomplete from "@/components/FormFields/TenantSearchAutocomplete.vue";

const store = useGlobalStore();
const router = useRouter();
const route = useRoute();

const {t} = useI18n();

const isAdminOrRoot = ref(store.user?.app.role === Role.Admin || store.user?.app.role === Role.Root);
const showCreateInstitution = ref(false);
const loadingData = ref(false);
const institutions: Ref<Institution[]> = ref([]);
const paginatedData: Ref<Paginated<Institution> | undefined> = ref();
let paginationFooterData: FooterData = footerDataFactory();
const searchString = ref(route.query.search as string);
const lockTenantFilter = computed(() => !!props.tenant);
const initialized = ref(false);

const props = defineProps({
  tenant: {
    type: Object as PropType<Tenant>,
    required: false
  },
  hiddenFields: {
    type: Array as PropType<string[]>,
    default: (): string[] => []
  },
  clickRoute: {
    type: String as PropType<string>,
    default: "institutionDetail"
  },
  routeParams: {
    type: Object as PropType<Record<string, unknown>>,
    default: () => ({})
  }
});

const cols: ColumnsConfig = {
  id: {
    title: t("common._id"),
    modelKey: "_id",
    visible: false,
    filter: ""
  },
  title: {
    title: t("common.title"),
    modelKey: "title",
    visible: true,
    filter: ""
  },
  tenant: {
    title: t("common.entities.Tenant"),
    modelKey: "tenantMeta.title",
    visible: true,
    filter: ""
  },
  createdAt: {
    title: t("common.created_at"),
    modelKey: "createdAt",
    visible: true,
    filter: ""
  }
};

const {
  columns,
  headers,
  page,
  perPage,
  sort,
  selectedTenant,
  activeFilterCount,
  resetFilter,
  createUrlFilterParameters,
  onUpdatePerPage,
  onSort,
  onNext,
  onPrev,
  onPageChange,
  setupFooterData,
  setupBasicParams
} = usePagination(fetchPage, {
  initialColumns: cols,
  initialSort: [{
    key: "createdAt",
    order: "asc"
  }]
});

/**
 * Watch for changes to the search query param when using global search
 */
watch(() => route.query.search, async (newSearchValue) => {
  if (newSearchValue && newSearchValue !== searchString.value) {
    resetFilter(props.hiddenFields);
    handleSearch(newSearchValue as string);
  }
});

onMounted(async () => {
  loadingData.value = true;
  if (searchString.value) {
    handleSearch(searchString.value);
    return;
  }

  setupBasicParams(page, perPage, sort, route);
  columns.title.filter = route.query.title as string || columns.title.filter;

  if (props.tenant && props.tenant._id) {
    columns.tenant.filter = props.tenant._id;
    selectedTenant.value = props.tenant._id;
  } else {
    columns.tenant.filter = route.query.filterTenant as string || columns.tenant.filter;
    selectedTenant.value = route.query.filterTenant as string || null;
  }

  initialized.value = true;
  await fetchPage();

  loadingData.value = false;
});

async function fetchPage() {
  loadingData.value = true;

  const params: InstitutionPaginationParams = {
    page: page.value,
    size: perPage.value,
    sortBy: sort.value[0].key,
    sortOrder: sort.value[0].order,

    title: columns.title.filter as string || undefined,
    filterTenant: columns.tenant.filter as string || undefined,
  };

  try {
    store.networkBusy = true;
    paginatedData.value = (await yapi.admin.institution.paginate(params)).data;

    await router.push({query: createUrlFilterParameters(params)});

    if (paginatedData.value)
      institutions.value = paginatedData.value.docs;
    paginationFooterData = setupFooterData(paginatedData.value);
  } catch (e) {
    if (isYapiError(e)) {
      const yError = e as YapiError;
      await store.handleYapiError(yError);
    } else {
      throw e;
    }
  } finally {
    store.networkBusy = false;
    loadingData.value = false;
  }
}

async function onCloseCreateDialog() {
  showCreateInstitution.value = false;
  await fetchPage();
}

const onClickRow = async (event: Event, row: { internalItem: { raw?: Institution } }) => {
  try {
    if (row.internalItem && row.internalItem.raw) {
      const institution = row.internalItem.raw;
      if (institution._id) {
        await navigateToInstitution(institution);
      }
    }
  } catch (e) {
    if (isYapiError(e)) {
      const yError = e as YapiError;
      await store.handleYapiError(yError);
    } else {
      throw e;
    }
  }
};

function handleReset() {
  searchString.value = "";
  resetFilter(props.hiddenFields);

  if (props.tenant && props.tenant._id) {
    columns.tenant.filter = props.tenant._id;
  }
}

function handleSearch(searchText: string) {
  columns.title.filter = searchText;
  searchString.value = searchText;
}

const navigateToInstitution = async (institution: Institution) => {
  const routeName = props.clickRoute || "institutionDetail";

  if (routeName === "institutionDashboard") {
    return await router.push({
      name: "institutionDashboard",
      params: {
        tenantId: institution.tenantMeta?._id,
        institutionId: institution._id
      }
    });
  }

  await router.push({
    name: "institutionDetail",
    params: {
      ...props.routeParams,
      institutionId: institution._id
    }
  });
};

// Keep the old function for backward compatibility with the action icon
const openInstitutionDetails = async (id: string) => {
  await router.push({
    name: "institutionDetail",
    params: {id}
  });
};
</script>
